import { CACHE_TTL, CACHE_ENABLED, CACHE_EXCLUDE_KEYWORDS, CACHE_DEBUG, CACHE_STORAGE } from '@/config/cacheConfig'

/**
 * 前端查询缓存（命中即直接返回，不发请求，省带宽）
 * - GET 查询：命中且未过期 → 直接返回缓存，不发请求
 * - 增/删/改成功后：按资源名失效相关缓存，下次查询重写为最新数据
 * - 存储位置由 cacheConfig.js 的 CACHE_STORAGE 决定：
 *     'memory'  仅内存（刷新页面即清空，数据永远最新）
 *     'session' sessionStorage（刷新仍在、关闭标签页清空）—— 默认
 *     'local'   localStorage（持久保存直到过期/手动清除）
 */

// key -> { data, expireAt }，页面运行期内始终以此 Map 为准
const store = new Map()

// ===== 持久化（可选）：把缓存同步写入 session/localStorage，刷新后回填，跨刷新也能命中 =====
const PERSIST_PREFIX = 'qcweb_cache:'

function resolveBackend() {
  try {
    if (CACHE_STORAGE === 'session' && typeof sessionStorage !== 'undefined') return sessionStorage
    if (CACHE_STORAGE === 'local' && typeof localStorage !== 'undefined') return localStorage
  } catch (e) { /* 隐私模式等可能抛错：退化为纯内存 */ }
  return null
}
const backend = resolveBackend()

function persistEntry(key, entry) {
  if (!backend) return
  try { backend.setItem(PERSIST_PREFIX + key, JSON.stringify(entry)) } catch (e) { /* 配额不足：忽略，退化为纯内存 */ }
}
function unpersistEntry(key) {
  if (!backend) return
  try { backend.removeItem(PERSIST_PREFIX + key) } catch (e) {}
}
function clearPersisted() {
  if (!backend) return
  const drop = []
  for (let i = 0; i < backend.length; i++) {
    const k = backend.key(i)
    if (k && k.indexOf(PERSIST_PREFIX) === 0) drop.push(k)
  }
  drop.forEach(k => { try { backend.removeItem(k) } catch (e) {} })
}
// 模块加载时从持久化存储回填内存（丢弃过期/损坏项）
;(function hydrate() {
  if (!backend) return
  const now = Date.now()
  const drop = []
  for (let i = 0; i < backend.length; i++) {
    const k = backend.key(i)
    if (!k || k.indexOf(PERSIST_PREFIX) !== 0) continue
    const cacheKey = k.slice(PERSIST_PREFIX.length)
    try {
      const entry = JSON.parse(backend.getItem(k))
      if (entry && typeof entry.expireAt === 'number' && entry.expireAt > now) {
        store.set(cacheKey, entry)
      } else {
        drop.push(k)
      }
    } catch (e) { drop.push(k) }
  }
  drop.forEach(k => { try { backend.removeItem(k) } catch (e) {} })
})()

// ===== 跨标签页即时同步（仅 localStorage 模式）=====
// storage 事件只在“其他”标签页写入/移除时触发；本标签页已直接改过内存 Map。
// 后台在任一标签页改动 -> 该页清缓存并写/删 localStorage -> 其他已打开标签页
// 收到事件 -> 同步更新各自内存 Map，从而刷新即见最新数据（无需等待 TTL 过期）。
if (typeof window !== 'undefined' && CACHE_STORAGE === 'local' && backend) {
  window.addEventListener('storage', (e) => {
    if (!e.key || e.key.indexOf(PERSIST_PREFIX) !== 0) return
    const cacheKey = e.key.slice(PERSIST_PREFIX.length)
    if (e.newValue == null) {
      store.delete(cacheKey)
      return
    }
    try {
      const entry = JSON.parse(e.newValue)
      if (entry && typeof entry.expireAt === 'number' && entry.expireAt > Date.now()) {
        store.set(cacheKey, entry)
      } else {
        store.delete(cacheKey)
      }
    } catch (err) {
      store.delete(cacheKey)
    }
  })
}

// 参数稳定序列化（key 排序，保证相同参数生成相同缓存 key）
function stableStringify(obj) {
  if (obj == null) return ''
  if (typeof obj !== 'object') return String(obj)
  return Object.keys(obj)
    .sort()
    .map(k => {
      const v = obj[k]
      const sv = v && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')
      return encodeURIComponent(k) + '=' + encodeURIComponent(sv)
    })
    .join('&')
}

export function makeCacheKey(url, params) {
  const qs = stableStringify(params)
  return url + (qs ? '?' + qs : '')
}

// 判断请求是否可缓存
export function isCacheable(config) {
  if (!CACHE_ENABLED) return false
  if ((config.method || 'get').toLowerCase() !== 'get') return false
  if (config.skipCache) return false
  // 二进制响应（PDF 预览 / 下载等）不缓存
  const rt = config.responseType
  if (rt && rt !== 'json' && rt !== 'text') return false
  const url = config.url || ''
  if (CACHE_EXCLUDE_KEYWORDS.some(k => url.includes(k))) return false
  return true
}

export function getCache(key) {
  const hit = store.get(key)
  if (!hit) return undefined
  if (Date.now() > hit.expireAt) {
    store.delete(key)
    unpersistEntry(key)
    return undefined
  }
  return hit.data
}

export function setCache(key, data) {
  const entry = { data, expireAt: Date.now() + CACHE_TTL }
  store.set(key, entry)
  persistEntry(key, entry)
  prune()
}

// 容量保护：超过 200 条时顺手清理过期项
function prune() {
  if (store.size <= 200) return
  const now = Date.now()
  for (const [k, v] of store) {
    if (now > v.expireAt) {
      store.delete(k)
      unpersistEntry(k)
    }
  }
}

// 从 URL 提取资源名：/api/admin/products/5 -> products；/api/products -> products
export function resourceToken(url) {
  if (!url) return null
  const segs = url.split('?')[0].split('/').filter(Boolean)
  const idx = segs.indexOf('api')
  if (idx === -1) return null
  let token = segs[idx + 1]
  if (token === 'admin') token = segs[idx + 2]
  return token || null
}

// 资源联动：改动某资源时，连带清除依赖它的资源缓存，避免隐蔽的脏数据
const RELATED_RESOURCES = {
  categories: ['products'],   // 改分类 → 产品列表/详情展示可能变
  industries: ['solutions']   // 改行业 → 解决方案展示可能变
}

// 失效：清除某资源（含联动资源）相关的所有缓存
export function invalidateResource(url) {
  const token = resourceToken(url)
  if (!token) {
    store.clear()
    clearPersisted()
    return
  }
  const tokens = [token, ...(RELATED_RESOURCES[token] || [])]
  for (const key of [...store.keys()]) {
    if (tokens.some(t => key.includes('/' + t))) {
      store.delete(key)
      unpersistEntry(key)
    }
  }
}

export function clearAllCache() {
  store.clear()
  clearPersisted()
}

export function cacheSize() {
  return store.size
}

// ===== 调试日志（可选）：在浏览器 Console 直观区分“走缓存 / 走后端 / 失效” =====
// 开启方式二选一：
//   1) 把 cacheConfig.js 里的 CACHE_DEBUG 改为 true（需重新打包）；
//   2) 运行时在控制台执行 localStorage.setItem('CACHE_DEBUG','1') 后刷新（无需打包，关闭用 '0'）。
export function cacheDebugEnabled() {
  if (CACHE_DEBUG) return true
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem('CACHE_DEBUG') === '1'
  } catch (e) {
    return false
  }
}

const DEBUG_LABELS = {
  HIT: '命中缓存·不发请求',
  MISS: '未命中·请求后端',
  WRITE: '写入缓存',
  INVALIDATE: '失效·后台改动触发'
}
const DEBUG_STYLES = {
  HIT: 'color:#16a34a;font-weight:bold',
  MISS: 'color:#2563eb',
  WRITE: 'color:#7c3aed',
  INVALIDATE: 'color:#ea580c;font-weight:bold'
}

export function cacheDebugLog(type, detail) {
  if (!cacheDebugEnabled()) return
  // eslint-disable-next-line no-console
  console.log('%c[缓存:' + (DEBUG_LABELS[type] || type) + '] ' + (detail || ''), DEBUG_STYLES[type] || '')
}